import { DataKeys } from "../../types";
import {
    AimmaProductId,
    isAimmaProductId,
    getLocalized,
    HealthLang,
} from "./healthTypes";
import { HealthLifecycleStageChip } from "./HealthLifecycleStageChip";
import { HealthStageLineMark, HealthStageNumberBadge } from "./HealthVisualMarks";
import {
    healthBody,
    healthLifecycleSelectorActive,
    healthLifecycleSelectorBase,
    healthLifecycleSelectorInactive,
    healthLifecycleStageCardActive,
    healthLifecycleStageCardBase,
    healthLifecycleStageCardInactive,
    healthFocusVisible,
} from "./healthVisualStyles";

interface HealthLifecycleStageCardProps {
    stage: DataKeys;
    index: number;
    isActive: boolean;
    onSelect: () => void;
    lang: HealthLang;
    showSummary?: boolean;
    showModuleChips?: boolean;
    /** Desktop timeline: inactive steps use compact reference-style rows */
    timelineCompact?: boolean;
}

export function HealthLifecycleStageCard({
    stage,
    index,
    isActive,
    onSelect,
    lang,
    showSummary = true,
    showModuleChips = false,
    timelineCompact = false,
}: HealthLifecycleStageCardProps) {
    const stepLabel = getLocalized(stage?.label, lang);
    const stepTitle = getLocalized(stage?.title, lang);
    const summary = getLocalized(stage?.desc, lang);
    const stageId = stage?.id as string | undefined;
    const relatedModules: string[] = stage?.relatedModules ?? [];

    const scrollToModules = () => {
        document
            .getElementById("product-suite")
            ?.scrollIntoView({ behavior: "smooth" });
    };

    if (timelineCompact && !isActive) {
        return (
            <button
                type="button"
                onClick={onSelect}
                className={`${healthLifecycleSelectorBase} ${healthLifecycleSelectorInactive} ${healthFocusVisible} w-full`}
            >
                <div className="text-xs font-semibold tracking-wide text-grey-400">
                    {stepLabel}
                </div>
                <div className="mt-1 text-base font-semibold text-grey-500 sm:text-lg">{stepTitle}</div>
            </button>
        );
    }

    if (timelineCompact && isActive) {
        return (
            <button
                type="button"
                onClick={onSelect}
                aria-current="step"
                className={`${healthLifecycleStageCardBase} ${healthLifecycleStageCardActive} ${healthFocusVisible} w-full text-left`}
            >
                <div className="mb-3 flex items-center gap-3">
                    <HealthStageLineMark
                        stageId={stageId}
                        index={index}
                        active
                    />
                    <HealthStageNumberBadge label={stepLabel} size="md" active />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-500">
                    Stage {stepLabel}
                </p>
                <h4 className="mt-1 text-base font-semibold leading-snug text-grey-800 sm:text-lg">
                    {stepTitle}
                </h4>
                {summary && (
                    <p className={`${healthBody} mt-3 line-clamp-3 text-sm text-grey-500`}>
                        {summary}
                    </p>
                )}
                {showModuleChips && relatedModules.filter(isAimmaProductId).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5 opacity-90">
                        {relatedModules.filter(isAimmaProductId).map((productId) => (
                            <HealthLifecycleStageChip
                                key={productId}
                                productId={productId as AimmaProductId}
                                lang={lang}
                                onNavigateToModules={scrollToModules}
                            />
                        ))}
                    </div>
                )}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={onSelect}
            aria-current={isActive ? "step" : undefined}
            className={`${healthLifecycleStageCardBase} ${healthFocusVisible} w-full text-left ${
                isActive
                    ? healthLifecycleStageCardActive
                    : healthLifecycleStageCardInactive
            }`}
        >
            <div className="flex gap-4">
                <HealthStageNumberBadge
                    label={stepLabel}
                    size="md"
                    active={isActive}
                />
                <div className="min-w-0 flex-1">
                    <p
                        className={`text-xs font-semibold uppercase tracking-wide ${
                            isActive ? "text-teal-500" : "text-grey-400"
                        }`}
                    >
                        Stage {stepLabel}
                    </p>
                    <h4
                        className={`mt-1 text-base font-semibold leading-snug sm:text-lg ${
                            isActive ? "text-grey-800" : "text-grey-600"
                        }`}
                    >
                        {stepTitle}
                    </h4>
                    {showSummary && summary && (
                        <p
                            className={`${healthBody} mt-2 line-clamp-2 text-sm ${
                                isActive ? "text-grey-500" : "text-grey-400"
                            }`}
                        >
                            {summary}
                        </p>
                    )}
                    {showModuleChips &&
                        isActive &&
                        relatedModules.filter(isAimmaProductId).length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5 opacity-90">
                                {relatedModules.filter(isAimmaProductId).map((productId) => (
                                    <HealthLifecycleStageChip
                                        key={productId}
                                        productId={productId as AimmaProductId}
                                        lang={lang}
                                        onNavigateToModules={scrollToModules}
                                    />
                                ))}
                            </div>
                        )}
                </div>
            </div>
        </button>
    );
}
